/**
 * Email: how report emails are sent — SMTP (Office 365 today) or Azure
 * Communication Services (after the Azure move) — and who receives the
 * reports of a bot that has no recipients of its own.
 *
 * Deployment-wide and admin only, like AI Provider. Every save is checked
 * first (an SMTP login, or a signed ACS lookup that sends nothing), so a wrong
 * password fails here instead of at the next scheduled report. The password
 * and the connection string never come back from the server: their fields stay
 * empty until someone types a new value.
 */

import { useCallback, useEffect, useState } from 'react';

import { api } from '../api/client';
import type {
  EmailProviderName,
  EmailSettings,
  EmailSettingsUpdate,
  SettingSource,
} from '../api/types';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  PageHeader,
  Spinner,
} from '../components/ui';
import { useAuth } from '../hooks/useAuth';
import { formatDateTime } from '../lib/format';

const PROVIDER_LABELS: Record<EmailProviderName, string> = {
  smtp: 'SMTP (Office 365)',
  acs: 'Azure Communication Services',
};

const INPUT =
  'mt-1 block w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm';

interface Draft {
  provider: EmailProviderName;
  smtpHost: string;
  smtpPort: string;
  smtpStarttls: boolean;
  smtpUser: string;
  smtpPassword: string;
  smtpFrom: string;
  acsConnection: string;
  acsSender: string;
  reportRecipient: string;
}

function draftFrom(settings: EmailSettings): Draft {
  return {
    provider: settings.provider,
    smtpHost: settings.smtp_host,
    smtpPort: String(settings.smtp_port),
    smtpStarttls: settings.smtp_starttls,
    smtpUser: settings.smtp_user,
    smtpPassword: '',
    smtpFrom: settings.smtp_from,
    acsConnection: '',
    acsSender: settings.acs_sender,
    reportRecipient: settings.report_recipient,
  };
}

/** Only what changed, so an untouched secret is never re-sent or cleared. */
function changes(draft: Draft, settings: EmailSettings): EmailSettingsUpdate {
  const update: EmailSettingsUpdate = {};
  if (draft.provider !== settings.provider) update.provider = draft.provider;
  if (draft.smtpHost.trim() !== settings.smtp_host)
    update.smtp_host = draft.smtpHost.trim();
  if (Number(draft.smtpPort) !== settings.smtp_port)
    update.smtp_port = Number(draft.smtpPort);
  if (draft.smtpStarttls !== settings.smtp_starttls)
    update.smtp_starttls = draft.smtpStarttls;
  if (draft.smtpUser.trim() !== settings.smtp_user)
    update.smtp_user = draft.smtpUser.trim();
  if (draft.smtpPassword) update.smtp_password = draft.smtpPassword;
  if (draft.smtpFrom.trim() !== settings.smtp_from)
    update.smtp_from = draft.smtpFrom.trim();
  if (draft.acsConnection.trim())
    update.acs_connection_string = draft.acsConnection.trim();
  if (draft.acsSender.trim() !== settings.acs_sender)
    update.acs_sender = draft.acsSender.trim();
  const recipients = recipientList(draft.reportRecipient);
  if (recipients !== settings.report_recipient) update.report_recipient = recipients;
  return update;
}

/** "a@x, b@y" from commas, semicolons or new lines, as the server stores it. */
function recipientList(value: string): string {
  return value
    .split(/[,;\n]/)
    .map((part) => part.trim())
    .filter(Boolean)
    .join(', ');
}

function problem(draft: Draft): string | null {
  const port = Number(draft.smtpPort);
  if (!Number.isInteger(port) || port < 1 || port > 65535)
    return 'SMTP port must be a whole number from 1 to 65535.';
  return null;
}

function errorText(caught: unknown, fallback: string): string {
  return caught instanceof Error && typeof caught.message === 'string'
    ? caught.message
    : fallback;
}

export function Email(): JSX.Element {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [settings, setSettings] = useState<EmailSettings | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [testTo, setTestTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<'save' | 'test' | 'reset' | 'clear' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const apply = useCallback((next: EmailSettings) => {
    setSettings(next);
    setDraft(draftFrom(next));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      apply(await api.emailSettings());
      setError(null);
    } catch (caught) {
      setError(errorText(caught, 'Could not load the email settings'));
    } finally {
      setLoading(false);
    }
  }, [apply]);

  useEffect(() => {
    if (isAdmin) void load();
  }, [isAdmin, load]);

  async function run(
    kind: NonNullable<typeof busy>,
    action: () => Promise<EmailSettings | string>,
  ): Promise<void> {
    setBusy(kind);
    setError(null);
    setNotice(null);
    try {
      const result = await action();
      if (typeof result === 'string') setNotice(result);
      else apply(result);
    } catch (caught) {
      setError(errorText(caught, 'The request failed'));
    } finally {
      setBusy(null);
    }
  }

  if (!isAdmin) {
    return (
      <Card>
        <EmptyState
          title="Email settings are for administrators."
          hint="They hold the mail password every report is sent with."
        />
      </Card>
    );
  }
  if (loading) return <Spinner label="Loading email settings…" />;
  if (!settings || !draft) return <ErrorNotice message={error ?? 'Not available'} />;

  const pending = changes(draft, settings);
  const dirty = Object.keys(pending).length > 0;
  const set = (patch: Partial<Draft>): void => setDraft({ ...draft, ...patch });

  function handleSave(): void {
    const invalid = problem(draft!);
    if (invalid) {
      setError(invalid);
      return;
    }
    const switching = pending.provider
      ? ` Reports are now sent with ${PROVIDER_LABELS[pending.provider]}.`
      : '';
    void run('save', async () => {
      const next = await api.updateEmailSettings(pending);
      const onlyRecipients = Object.keys(pending).every(
        (key) => key === 'report_recipient',
      );
      setNotice(
        onlyRecipients
          ? 'Saved. The next reports go to the new recipients.'
          : `Saved and verified.${switching} Send a test email to be sure.`,
      );
      return next;
    });
  }

  function handleClear(field: 'smtp_password' | 'acs_connection_string'): void {
    const label = field === 'smtp_password' ? 'SMTP password' : 'ACS connection string';
    if (!window.confirm(`Remove the ${label} saved here and use the one in .env?`))
      return;
    void run('clear', async () => {
      const next = await api.updateEmailSettings({ [field]: '' });
      setNotice(`${label} removed; the .env value is in force.`);
      return next;
    });
  }

  function handleReset(): void {
    if (
      !window.confirm(
        'Remove every email setting saved on this page and go back to the .env configuration?',
      )
    )
      return;
    void run('reset', async () => {
      const next = await api.resetEmailSettings();
      setNotice('Back on the .env configuration.');
      return next;
    });
  }

  function handleTest(): void {
    void run('test', async () => {
      const result = await api.testEmailSettings(testTo.trim() || undefined);
      if (!result.ok) throw new Error(result.detail);
      return result.detail;
    });
  }

  return (
    <div>
      <PageHeader
        title="Email"
        description="How report emails are sent. Saving checks the settings with the mail service first."
        actions={
          <Button onClick={handleSave} disabled={!dirty || busy !== null}>
            {busy === 'save' ? 'Verifying…' : 'Save'}
          </Button>
        }
      />

      {error && (
        <div className="mb-4">
          <ErrorNotice message={error} />
        </div>
      )}
      {notice && (
        <div
          role="status"
          className="mb-4 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800"
        >
          {notice}
        </div>
      )}
      {settings.unreadable_overrides.length > 0 && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          A saved secret can no longer be decrypted (the server&apos;s JWT_SECRET
          changed), so the .env value is in use. Enter it again to fix it.
        </div>
      )}
      {!settings.configured && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {PROVIDER_LABELS[settings.provider]} is selected but not fully set up, so
          report emails are not being sent.
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <ProviderCard
          number="1"
          provider="smtp"
          selected={draft.provider === 'smtp'}
          active={settings.provider === 'smtp'}
          onSelect={() => set({ provider: 'smtp' })}
          summary={
            settings.smtp_host
              ? `${settings.smtp_host}:${settings.smtp_port}`
              : 'Not set up yet.'
          }
        >
          <div className="grid grid-cols-3 gap-3">
            <Field
              label="Server"
              source={settings.smtp_host_source}
              className="col-span-2"
            >
              <input
                spellCheck={false}
                value={draft.smtpHost}
                onChange={(event) => set({ smtpHost: event.target.value })}
                placeholder="smtp.office365.com"
                className={`${INPUT} font-mono`}
              />
            </Field>
            <Field label="Port" source={settings.smtp_port_source}>
              <input
                type="number"
                min={1}
                max={65535}
                value={draft.smtpPort}
                onChange={(event) => set({ smtpPort: event.target.value })}
                className={INPUT}
              />
            </Field>
          </div>
          <label className="mt-3 flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={draft.smtpStarttls}
              onChange={(event) => set({ smtpStarttls: event.target.checked })}
            />
            Use STARTTLS (required by Office 365)
            <SourceTag source={settings.smtp_starttls_source} />
          </label>
          <Field label="Username" source={settings.smtp_user_source}>
            <input
              spellCheck={false}
              autoComplete="off"
              value={draft.smtpUser}
              onChange={(event) => set({ smtpUser: event.target.value })}
              placeholder="formachat@formaspace.com"
              className={`${INPUT} font-mono`}
            />
          </Field>
          <Field
            label="Password"
            source={
              settings.smtp_password_set ? settings.smtp_password_source : undefined
            }
          >
            <input
              type="password"
              autoComplete="new-password"
              value={draft.smtpPassword}
              onChange={(event) => set({ smtpPassword: event.target.value })}
              placeholder={
                settings.smtp_password_set
                  ? 'Leave blank to keep the current password'
                  : 'Mailbox password or app password'
              }
              className={`${INPUT} font-mono`}
            />
          </Field>
          {settings.smtp_password_set &&
            settings.smtp_password_source === 'database' && (
              <ClearButton
                onClick={() => handleClear('smtp_password')}
                busy={busy !== null}
              />
            )}
          <Field label="Send as (From)" source={settings.smtp_from_source}>
            <input
              spellCheck={false}
              value={draft.smtpFrom}
              onChange={(event) => set({ smtpFrom: event.target.value })}
              className={`${INPUT} font-mono`}
            />
          </Field>
          <p className="mt-3 text-xs text-slate-500">
            Office 365: the mailbox needs SMTP AUTH turned on (Microsoft 365 admin → the
            user → Mail → Manage email apps → Authenticated SMTP). Error 5.7.57 means no
            username or password; 5.7.139 means SMTP AUTH is off; 5.7.3 means a wrong
            password.
          </p>
        </ProviderCard>

        <ProviderCard
          number="2"
          provider="acs"
          selected={draft.provider === 'acs'}
          active={settings.provider === 'acs'}
          onSelect={() => set({ provider: 'acs' })}
          summary={settings.acs_endpoint || 'Not set up yet.'}
        >
          <Field
            label="Connection string"
            source={
              settings.acs_connection_string_set
                ? settings.acs_connection_string_source
                : undefined
            }
          >
            <input
              type="password"
              autoComplete="off"
              spellCheck={false}
              value={draft.acsConnection}
              onChange={(event) => set({ acsConnection: event.target.value })}
              placeholder={
                settings.acs_connection_string_set
                  ? 'Leave blank to keep the current one'
                  : 'endpoint=https://….communication.azure.com/;accesskey=…'
              }
              className={`${INPUT} font-mono`}
            />
            <span className="mt-1 block text-xs text-slate-500">
              Azure portal → your Communication Services resource → Settings → Keys →
              Connection string.
            </span>
          </Field>
          {settings.acs_connection_string_set &&
            settings.acs_connection_string_source === 'database' && (
              <ClearButton
                onClick={() => handleClear('acs_connection_string')}
                busy={busy !== null}
              />
            )}
          <Field label="Send as (MailFrom address)" source={settings.acs_sender_source}>
            <input
              spellCheck={false}
              value={draft.acsSender}
              onChange={(event) => set({ acsSender: event.target.value })}
              placeholder="DoNotReply@….azurecomm.net"
              className={`${INPUT} font-mono`}
            />
            <span className="mt-1 block text-xs text-slate-500">
              An address on a domain connected to the resource (Email Communication
              Services → Provision domains). ACS refuses any other sender.
            </span>
          </Field>
        </ProviderCard>
      </div>

      <Card className="mt-4 p-5">
        <h2 className="text-base font-semibold text-slate-900">
          Default report recipients
          <SourceTag source={settings.report_recipient_source} />
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          Every bot&apos;s review reports go here, unless the bot has its own list under
          Settings → Report Recipients. Separate several addresses with commas.
        </p>
        <textarea
          rows={2}
          spellCheck={false}
          value={draft.reportRecipient}
          onChange={(event) => set({ reportRecipient: event.target.value })}
          placeholder="mktg@formaspace.com"
          className={`${INPUT} font-mono`}
        />
      </Card>

      <Card className="mt-4 p-5">
        <h2 className="text-base font-semibold text-slate-900">Send a test email</h2>
        <p className="mt-1 text-sm text-slate-600">
          Sends a real email with the saved settings (
          {PROVIDER_LABELS[settings.provider]}
          {settings.sender ? `, from ${settings.sender}` : ''}). Save changes first.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <input
            type="email"
            value={testTo}
            onChange={(event) => setTestTo(event.target.value)}
            placeholder={user?.email ?? 'you@formaspace.com'}
            className="block w-72 rounded-md border border-slate-300 px-3 py-1.5 text-sm"
          />
          <Button
            variant="secondary"
            onClick={handleTest}
            disabled={busy !== null || dirty}
          >
            {busy === 'test' ? 'Sending…' : 'Send test email'}
          </Button>
        </div>
      </Card>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4 text-xs text-slate-500">
        <span>
          {settings.updated_at
            ? `Last changed ${formatDateTime(settings.updated_at)} by ${settings.updated_by ?? 'unknown'}.`
            : 'Nothing saved here yet: everything comes from .env.'}
        </span>
        <Button
          variant="danger"
          onClick={handleReset}
          disabled={busy !== null || !settings.updated_at}
        >
          {busy === 'reset' ? 'Resetting…' : 'Reset to .env'}
        </Button>
      </div>
    </div>
  );
}

function SourceTag({ source }: { source: SettingSource }): JSX.Element {
  return (
    <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-500">
      {source === 'database' ? 'saved here' : 'from .env'}
    </span>
  );
}

function Field(props: {
  label: string;
  source?: SettingSource;
  className?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className={`mt-3 block text-sm ${props.className ?? ''}`}>
      <span className="font-medium text-slate-700">{props.label}</span>
      {props.source && <SourceTag source={props.source} />}
      {props.children}
    </label>
  );
}

function ClearButton(props: { onClick: () => void; busy: boolean }): JSX.Element {
  return (
    <button
      type="button"
      onClick={props.onClick}
      disabled={props.busy}
      className="mt-1 text-xs text-slate-500 underline hover:text-slate-700 disabled:no-underline"
    >
      Remove the value saved here
    </button>
  );
}

interface ProviderCardProps {
  number: string;
  provider: EmailProviderName;
  selected: boolean;
  active: boolean;
  onSelect: () => void;
  summary: string;
  children: React.ReactNode;
}

function ProviderCard(props: ProviderCardProps): JSX.Element {
  return (
    <Card className={`p-5 ${props.selected ? 'ring-2 ring-brand-blue' : ''}`}>
      <label className="flex cursor-pointer items-start gap-3">
        <input
          type="radio"
          name="email-provider"
          className="mt-1"
          checked={props.selected}
          onChange={props.onSelect}
        />
        <span>
          <span className="block text-base font-semibold text-slate-900">
            {props.number}. {PROVIDER_LABELS[props.provider]}
            {props.active && (
              <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
                In use
              </span>
            )}
          </span>
          <span className="block text-xs text-slate-500">{props.summary}</span>
        </span>
      </label>
      {props.children}
    </Card>
  );
}
